/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */

import { useEffect, useId } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { mergeAll } from "ramda";
import { createPortal } from "react-dom";
import * as ReactDOMClient from "react-dom/client";
import {
    createBrowserRouter,
    Link,
    Outlet,
    RouterProvider,
    useLocation,
    useNavigate,
    useRouteError,
} from "react-router-dom";
import { z } from "zod";

import useFCSubscriptionStore from "@hsl/fc-subscription/store";
import IsFCSEnabledContext from "@hsl/lgim-explorer/src/components/Subscription/IsFCSEnabledContext";
import { ManageMyFunds } from "@hsl/lgim-explorer/src/components/Subscription/ManageMyFunds";
import { MyFunds } from "@hsl/lgim-explorer/src/components/Subscription/MyFunds";
import { SubscriptionRoot } from "@hsl/lgim-explorer/src/components/Subscription/Root";
import {
    ShareFlow,
    SubscribeFlow,
    WatchFlow,
} from "@hsl/lgim-explorer/src/components/Subscription/SubscriptionFlows";
import { DACHScreen } from "@hsl/lgim-explorer/src/components/Subscription/SubscriptionFlows/DACH";
import { NewUserScreen } from "@hsl/lgim-explorer/src/components/Subscription/SubscriptionFlows/NewUser";
import SubscriptionNotification from "@hsl/lgim-explorer/src/components/Subscription/SubscriptionNotification";
import { SubscriptionTabButton } from "@hsl/lgim-explorer/src/components/Subscription/SubscriptionTabButton";
import type { Config } from "@hsl/lgim-explorer/src/config/types";
import {
    Translate,
    TranslationProvider,
} from "@hsl/lgim-explorer/src/hooks/useTranslation";
import FundExplorer from "@hsl/lgim-explorer/src/index";

const queryClient = new QueryClient();

declare global {
    interface Window {
        enableFCS?: boolean;
    }
}

try {
    if (!("enableFCS" in window))
        console.warn(
            "enableFCS not set, defaulting to showing the FCS service. To disable, set `window.enableFCS = false`.",
        );
    const enableFCS = window.enableFCS ?? true;
    const container = document.querySelector<HTMLDivElement>("#fc-root");
    if (!container) throw new Error("No #fc-root on page");

    const globalConfig = JSON.parse(container.dataset.config ?? "{}");

    const [pmcConfig, collectivesConfig, etfConfig] = [
        "pmc",
        "collectives",
        "etf",
    ].map((id) => {
        const fundCentreContainer = document.querySelector<HTMLDivElement>(
            `#fc-configs > #${id}`,
        );
        if (!fundCentreContainer)
            throw new Error(`No #${id} in #fc-configs on page`);
        const fundCentre =
            fundCentreContainer.querySelector<HTMLDivElement>("#root");
        if (!fundCentre) throw new Error(`No #root for ${id} on page`);
        const config: Config = JSON.parse(fundCentre.dataset.config ?? "{}");

        const audienceId = fundCentre.dataset.audienceId;
        config.audienceId = Number.parseInt(audienceId!);

        const jurisdictionId = fundCentre.dataset.jurisdictionId;
        config.jurisdictionId = Number.parseInt(jurisdictionId!);

        const usertypeId = fundCentre.dataset.usertypeId;
        config.usertypeId = Number.parseInt(usertypeId!);

        const languageId = fundCentre.dataset.languageId;
        config.languageId = Number.parseInt(languageId!);

        config.routeId = Number.parseInt(document.body.dataset.routeId!);

        const contactURL = fundCentre.dataset.contactUrl;
        config.contactURL = String(contactURL);

        // --------------------------------------------------------
        const PARAMS = new URLSearchParams(window.location.search);

        const activeShareClassISIN = PARAMS.get("isin_code");
        config.activeShareClassISIN = activeShareClassISIN;

        config.activeFilters = [];
        for (const [key, value] of PARAMS.entries()) {
            if (config.filters.find((x) => x.prop === key)) {
                config.activeFilters.push([key, value]);
            }
        }
        // --------------------------------------------------------

        // get banner url

        const banner =
            fundCentreContainer.querySelector<HTMLScriptElement>(
                ".simple > script",
            );
        if (banner) {
            const bannerSchema = z.object({
                src: z.string(),
            });
            const res = bannerSchema.safeParse(JSON.parse(banner.innerText));
            if (res.success) {
                config.banner = res.data.src;
            }
        }

        return { ...config, ...globalConfig };
    });

    if (!pmcConfig || !collectivesConfig || !etfConfig)
        throw new Error("No configs");

    let subscriptionBannerConfig = {} as {
        title?: string;
        text?: string;
        banner?: string;
    };

    const subscriptionBannerElem = document.querySelector<HTMLDivElement>(
        "#fc-subscription-banner",
    );

    if (subscriptionBannerElem) {
        const subscBanner =
            subscriptionBannerElem.querySelector<HTMLScriptElement>("script");

        if (subscBanner) {
            const bannerSchema = z.object({
                src: z.string(),
                title: z.string().optional(),
                text: z.string().optional(),
            });
            const res = bannerSchema.safeParse(
                JSON.parse(subscBanner.innerText),
            );
            if (res.success) {
                subscriptionBannerConfig = {
                    banner: res.data.src,
                    title: res.data.title,
                    text: res.data.text,
                };
            }
        }
    }

    // preload banner images to avoid flicker
    // simply sets a 0px x 0px ::before element with the banners as a background image
    const banner = document.querySelector<HTMLImageElement>(".banner-bg");
    if (banner) {
        banner.style.setProperty(
            "--preload",
            [pmcConfig, collectivesConfig, etfConfig, subscriptionBannerConfig]
                .map((x) => x.banner as string)
                .filter(Boolean)
                .map((x) => `url(${x})`)
                .join(" "),
        );
    }

    const translations = mergeAll(
        Array.from(
            document.querySelectorAll<HTMLScriptElement>(
                "#fc-translations script",
            ),
        )
            .map((script) => {
                try {
                    return JSON.parse(script.innerText);
                } catch (e) {
                    console.error("Failed to parse translations:", script);
                    return null;
                }
            })
            .filter((json) => Boolean(json)) as Record<string, string>[],
    );

    console.table(translations);

    const userTitles = (() => {
        const defaultTitles = ["Mr", "Mrs", "Miss", "Ms"];
        try {
            const json = JSON.parse(
                document.querySelector("#add-details-step-data")?.textContent ??
                    "{}",
            ) as {
                fields?: Record<string, Record<string, string | Array<string>>>;
            };
            const title = json?.fields?.Title;
            return title ? (title.choices as Array<string>) : defaultTitles;
        } catch (err) {
            console.error(err);
            return defaultTitles;
        }
    })();

    const basename = window.location.pathname.split("/").slice(0, 5).join("/");

    const tabContainerElem = document.querySelector(
        ".fund-centre-tabs-container",
    );

    const RootLayout = () => {
        const navigate = useNavigate();
        const id = useId();
        const init = useFCSubscriptionStore((store) => store.init);
        const dach = useFCSubscriptionStore((store) => store.dach);
        const user = useFCSubscriptionStore((store) => store.user);
        const { pathname } = useLocation();

        // scroll to top on root change
        // biome-ignore lint/correctness/useExhaustiveDependencies: we want to watch pathname changes
        useEffect(() => {
            window.scrollTo(0, 0);
        }, [pathname]);

        useEffect(() => {
            void init({ navigate }).catch(console.error);
        }, [init, navigate]);

        useEffect(() => {
            const tabs =
                document.querySelectorAll<HTMLAnchorElement>(
                    "a.fund-centre-tab",
                );

            function handleClick(this: HTMLAnchorElement, e: MouseEvent) {
                const url = new URL(this.href);
                if (url.host !== window.location.host) {
                    // probably the MPS tab
                    return;
                }
                e.preventDefault();
                let path = `/${url.pathname
                    .split("/")
                    .slice(5)
                    .filter((x) => !!x)
                    .join("/")}`;
                if (url.search) {
                    path += url.search;
                }
                tabs.forEach((tab) => tab.classList.remove("selected"));
                this.classList.add("selected");
                navigate(path);
            }

            tabs.forEach((tab) => {
                if (tab.dataset.modified) return;
                tab.dataset.modified = id;
                tab.addEventListener("click", handleClick);
            });

            return () => {
                tabs.forEach((tab) => {
                    if (tab.dataset.modified !== id) return;
                    tab.removeEventListener("click", handleClick);
                    tab.dataset.modified = "";
                });
            };
        }, [id, navigate]);

        const needsUserDetails = user && user.title === "-";

        if (!enableFCS) {
            return <Outlet />;
        }

        return (
            <>
                {tabContainerElem &&
                    createPortal(<SubscriptionTabButton />, tabContainerElem)}
                <SubscriptionNotification />
                {dach ? (
                    <DACHScreen />
                ) : needsUserDetails ? (
                    <NewUserScreen
                        user={user}
                        userTitles={userTitles}
                        config={collectivesConfig ?? pmcConfig ?? etfConfig}
                    />
                ) : (
                    <Outlet />
                )}
            </>
        );
    };

    const RootErrorScreen = () => {
        const error = useRouteError();
        const latestFC = useFCSubscriptionStore((state) => state.latestFC);

        useEffect(() => {
            console.error(error);
        }, [error]);

        return (
            <div className="container flex h-full flex-col items-center justify-center space-y-4 px-4 py-8">
                <h1 className="text-centre mb-4 text-2xl font-bold">
                    <Translate>Error</Translate>
                </h1>
                <p className="text-center">
                    <Translate>
                        Something went wrong. Please try again.
                    </Translate>
                </p>
                <Link to={latestFC} className="text-core-blue underline">
                    <Translate>Back to fund centre</Translate>
                </Link>
            </div>
        );
    };

    const router = createBrowserRouter(
        [
            {
                path: "/",
                element: <RootLayout />,
                errorElement: <RootErrorScreen />,
                children: [
                    {
                        index: true,
                        element: (
                            <FundExplorer
                                key="collectives"
                                config={collectivesConfig}
                            />
                        ),
                    },
                    {
                        path: "/pmc",
                        element: <FundExplorer key="pmc" config={pmcConfig} />,
                    },
                    {
                        path: "/etf",
                        element: <FundExplorer key="etf" config={etfConfig} />,
                    },
                    {
                        path: "/my-funds",
                        element: (
                            <SubscriptionRoot
                                enabled={enableFCS}
                                banner={subscriptionBannerConfig}
                                config={
                                    collectivesConfig ?? pmcConfig ?? etfConfig
                                }
                                audienceMeta={{ userTitles }}
                            />
                        ),
                        children: [
                            {
                                index: true,
                                element: (
                                    <MyFunds
                                        collectivesConfig={collectivesConfig}
                                        etfConfig={etfConfig}
                                        pmcConfig={pmcConfig}
                                    />
                                ),
                            },
                            {
                                path: "/my-funds/manage",
                                element: (
                                    <ManageMyFunds
                                        collectivesConfig={collectivesConfig}
                                        etfConfig={etfConfig}
                                        pmcConfig={pmcConfig}
                                    />
                                ),
                            },
                            {
                                path: "/my-funds/subscribe",
                                element: <SubscribeFlow />,
                            },
                            {
                                path: "/my-funds/watch",
                                element: <WatchFlow />,
                            },
                            {
                                path: "/my-funds/subscribe/share",
                                element: <ShareFlow mode="subscribe" />,
                            },
                            {
                                path: "/my-funds/watch/share",
                                element: <ShareFlow mode="watch" />,
                            },
                        ],
                    },
                ],
            },
        ],
        { basename },
    );

    const root = ReactDOMClient.createRoot(container);
    root.render(
        <IsFCSEnabledContext.Provider value={enableFCS}>
            <QueryClientProvider client={queryClient}>
                <TranslationProvider value={translations}>
                    <RouterProvider router={router} />
                </TranslationProvider>
            </QueryClientProvider>
        </IsFCSEnabledContext.Provider>,
    );
} catch (err) {
    console.error(err);
}
